import { Component, OnDestroy, OnInit } from '@angular/core';
import { Validators } from '@angular/forms';
import { AuthService, REMEMBER_ME } from '@core/services/auth.service';
import { BBIDService } from '@core/services/bbid.service';
import { LocationService } from '@core/services/location.service';
import { PortalDeterminationService } from '@core/services/portal-determination.service';
import { SpinnerService } from '@core/services/spinner.service';
import { SSOService } from '@core/services/sso.service';
import { DetermineSignInFlow, LoginScenario } from '@core/typings/auth.typing';
import { LoginBehaviors } from '@core/typings/login-behaviors.typing';
import { TypeSafeFormBuilder, TypeSafeFormGroup } from '@yourcause/common/core-forms';
import { EmailExtensionValidator } from '@yourcause/common/form-control-validation';
import { I18nService } from '@yourcause/common/i18n';
import { NotifierService } from '@yourcause/common/notifier';
import { PanelTypes } from '@yourcause/common/panel';
import { Subscription } from 'rxjs';

export interface LoginGroup {
  username: string;
  rememberMe: boolean;
}

@Component({
    selector: 'gc-login-form',
    templateUrl: './login-form.component.html',
    styleUrls: ['./login-form.component.scss'],
    standalone: false
})
export class LoginFormComponent implements OnInit, OnDestroy {
  PanelTypes = PanelTypes;
  formGroup: TypeSafeFormGroup<LoginGroup>;
  rememberMe: boolean;
  ssoLoginUrl: string;
  loggingInWithSSO = false;
  showConvertToBbidText = false;
  helpCenterLink = 'https://webfiles-sc1.blackbaud.com/files/support/helpfiles/grantsconnect/content/gc-blackbaud-id.html';
  bbidHelpText = 'Learn more about <a href="__helpCenterLink__" target="_blank">BBID authentication</a> in our Help Center.';
  sub = new Subscription();

   constructor (
    private formBuilder: TypeSafeFormBuilder,
    private authService: AuthService,
    private spinnerService: SpinnerService,
    private portal: PortalDeterminationService,
    private ssoService: SSOService,
    private notifier: NotifierService,
    private i18n: I18nService,
    private locationService: LocationService,
    private bbidService: BBIDService
  ) {
     this.sub.add(this.ssoService.changesTo$('ssoConfig').subscribe(ssoConfig => {
      if (ssoConfig && ssoConfig.loginBehavior !== LoginBehaviors.PlainLogin) {
        this.ssoLoginUrl = ssoConfig.ssoLoginUrl;

        if (ssoConfig.loginBehavior === LoginBehaviors.SSO) {
          this.loginWithSSO();
        }
      }
    }));
  }

  get isApply () {
    return this.portal.isApply;
  }

  get loginScenario () {
    return this.authService.loginScenario;
  }

  async ngOnInit () {
    const cache = localStorage.getItem(REMEMBER_ME);
    const rememberMe = !!cache;
    let username = this.authService.username;
    if (rememberMe && !username) {
      username = cache;
      this.authService.setUsername(cache);
    }
    this.formGroup = this.formBuilder.group({
      username: [username, [Validators.required, EmailExtensionValidator]],
      rememberMe: [rememberMe]
    });

    this._onSubmit = this._onSubmit.bind(this);
  }

  loginWithSSO () {
    this.spinnerService.startSpinner();
    this.loggingInWithSSO = true;
    setTimeout(() => {
      location.href = this.ssoLoginUrl;
    }, 50);
  }

  openHelpLink () {
    const link = 'https://webfiles.blackbaud.com/files/support/helpfiles/grantsconnect/content/gc-troubleshoot-login.html';
    this.locationService.openExternalUrl(link);
  }

  rememberMeChange (rememberMe: boolean) {
    if (rememberMe) {
      localStorage.setItem(REMEMBER_ME, this.authService.username ?? '');
    } else {
      localStorage.removeItem(REMEMBER_ME);
    }
  }

  setUsername (username: string) {
    username = (username || '').trim();
    this.authService.setUsername(username);
    if (this.formGroup.value.rememberMe) {
      this.rememberMeChange(this.formGroup.value.rememberMe);
    }
  }

  async _onSubmit () {
    this.spinnerService.startSpinner();
    await this.authService.handleInitialLogin();
    this.proceedWithLoginScenario();
    this.spinnerService.stopSpinner();
  }

  proceedWithLoginScenario () {
    switch (this.loginScenario) {
      case LoginScenario.SSO_USER:
        this.loginWithSSO();
        break;
      case LoginScenario.UNKNOWN_EMAIL:
        this.notifier.error(this.i18n.translate(
          this.isApply ?
            'common:textEmailUnknownApplicant' :
            'common:textEmailUnknownManager2',
          {
            email: this.formGroup.value.username
          },
          this.isApply ?
            `The email entered is not associated with an account. Click "Create account" to get started with a new account.` :
            `We cannot find an account for __email__. Contact your GrantsConnect admin to create one.`
        ));
        break;
      case LoginScenario.NEW_BBID_USER:
        this.showConvertToBbidText = this.loginScenario === LoginScenario.NEW_BBID_USER;
        break;
      case LoginScenario.EXISTING_BBID_USER:
        this.continueToBbid();
        break;
    }
  }

  continueToBbid () {
    this.bbidService.redirectToBbid(
      this.authService.signInFlow,
      this.authService.username,
      false
    );
  }

  createAccount () {
    const signInFlowForBrandNew: DetermineSignInFlow = {
      hasExistingBbidAccount: false,
      isBbidLinked: false,
      emailExists: false,
      isSSO: false,
      ssoId: null
    };
    this.bbidService.redirectToBbid(signInFlowForBrandNew, '', true);
  }

  ngOnDestroy () {
    this.sub.unsubscribe();
  }
}
